/**
 * Impostazioni azienda — pagina admin.
 * Modifica logo, colori, ragione sociale, P.IVA, sede, contatti.
 * Applica il branding in tempo reale grazie a CompanySettingsContext.
 */
import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Building2, Upload, Save, Image as ImageIcon, Palette } from "lucide-react";
import api from "@/api";
import { useCompanySettings } from "@/context/CompanySettingsContext";
import WebdeskPanel from "@/pages/utenti/WebdeskPanel";
import ContabilitaPasswordPanel from "@/pages/utenti/ContabilitaPasswordPanel";
import ZipDownloadPanel from "@/pages/utenti/ZipDownloadPanel";

const FIELDS = [
  { k: "ragione_sociale", l: "Ragione sociale *", section: "identita" },
  { k: "nome_breve", l: "Nome breve (logo testuale)", section: "identita" },
  { k: "partita_iva", l: "Partita IVA", section: "fiscale" },
  { k: "codice_fiscale", l: "Codice fiscale", section: "fiscale" },
  { k: "indirizzo_sede", l: "Indirizzo sede legale", section: "sede" },
  { k: "comune_sede", l: "Comune", section: "sede" },
  { k: "cap_sede", l: "CAP", section: "sede" },
  { k: "provincia_sede", l: "Provincia (sigla)", section: "sede" },
  { k: "telefono", l: "Telefono", section: "contatti" },
  { k: "email", l: "Email", section: "contatti" },
  { k: "pec", l: "PEC", section: "contatti" },
  { k: "sito_web", l: "Sito web", section: "contatti" },
];

const SECTION_LABELS = {
  identita: "Identità",
  fiscale: "Dati fiscali",
  sede: "Sede legale",
  contatti: "Contatti",
};

export default function ImpostazioniAzienda() {
  const { company, refresh } = useCompanySettings();
  const [form, setForm] = useState(company);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    setForm(company);
  }, [company]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleLogo = (file) => {
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      toast.error("Logo troppo grande (max 2MB)");
      return;
    }
    if (!/^image\/(png|jpeg|jpg|svg\+xml|webp)$/.test(file.type)) {
      toast.error("Formato non supportato: usa PNG, JPG, SVG o WebP");
      return;
    }
    setUploading(true);
    const reader = new FileReader();
    reader.onload = () => {
      set("logo_base64", reader.result);
      setUploading(false);
      toast.success("Logo caricato — ricorda di salvare");
    };
    reader.onerror = () => {
      setUploading(false);
      toast.error("Lettura file fallita");
    };
    reader.readAsDataURL(file);
  };

  const save = async () => {
    if (!form.ragione_sociale) {
      toast.error("La ragione sociale è obbligatoria");
      return;
    }
    setSaving(true);
    try {
      await api.put("/settings/company", form);
      toast.success("Impostazioni salvate");
      await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Salvataggio fallito");
    } finally {
      setSaving(false);
    }
  };

  const groupedFields = Object.keys(SECTION_LABELS).map((sec) => ({
    section: sec,
    label: SECTION_LABELS[sec],
    fields: FIELDS.filter((f) => f.section === sec),
  }));

  return (
    <div className="space-y-6" data-testid="impostazioni-azienda-page">
      <div>
        <div className="overline">Amministrazione</div>
        <h1 className="font-display text-3xl xl:text-4xl font-black flex items-center gap-2">
          <Building2 className="w-7 h-7 text-slate-500" /> Impostazioni Azienda
        </h1>
        <p className="text-xs text-slate-500 mt-1">
          Logo, colori, dati fiscali. Il branding viene applicato subito a tutta l'app dopo il salvataggio.
        </p>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* COLONNA SX: dati testuali */}
        <div className="lg:col-span-2 space-y-6">
          {groupedFields.map((grp) => (
            <section key={grp.section} className="bg-white border border-slate-200 rounded-md p-5">
              <div className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-3">{grp.label}</div>
              <div className="grid md:grid-cols-2 gap-4">
                {grp.fields.map((f) => (
                  <div key={f.k}>
                    <label className="text-xs font-semibold text-slate-600 block mb-1">{f.l}</label>
                    <input
                      value={form[f.k] || ""}
                      onChange={(e) => set(f.k, e.target.value)}
                      data-testid={`imp-${f.k}`}
                      className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm"
                    />
                  </div>
                ))}
              </div>
            </section>
          ))}

          <section className="bg-white border border-slate-200 rounded-md p-5">
            <div className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-3 flex items-center gap-2">
              <Palette className="w-4 h-4" /> Colori brand
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-semibold text-slate-600 block mb-1">Colore primario</label>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={form.colore_primario || "#0f172a"}
                    onChange={(e) => set("colore_primario", e.target.value)}
                    data-testid="imp-colore_primario"
                    className="w-14 h-10 border border-slate-300 rounded-sm cursor-pointer p-0"
                  />
                  <input
                    type="text"
                    value={form.colore_primario || ""}
                    onChange={(e) => set("colore_primario", e.target.value)}
                    className="flex-1 px-3 py-2 border border-slate-300 rounded-sm text-sm font-mono"
                    placeholder="#0f172a"
                  />
                </div>
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-600 block mb-1">Colore accento</label>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={form.colore_accento || "#f97316"}
                    onChange={(e) => set("colore_accento", e.target.value)}
                    data-testid="imp-colore_accento"
                    className="w-14 h-10 border border-slate-300 rounded-sm cursor-pointer p-0"
                  />
                  <input
                    type="text"
                    value={form.colore_accento || ""}
                    onChange={(e) => set("colore_accento", e.target.value)}
                    className="flex-1 px-3 py-2 border border-slate-300 rounded-sm text-sm font-mono"
                    placeholder="#f97316"
                  />
                </div>
              </div>
            </div>
          </section>

          <section className="bg-white border border-slate-200 rounded-md p-5">
            <div className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-3">Note in calce</div>
            <textarea
              rows={3}
              value={form.footer_text || ""}
              onChange={(e) => set("footer_text", e.target.value)}
              placeholder="Es. Iscrizione CCIAA · Capitale Sociale · Legale Rappresentante"
              data-testid="imp-footer"
              className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm"
            />
          </section>

          {/* Cambio password sezione Contabilità */}
          <ContabilitaPasswordPanel />

          {/* Download ZIP produzione */}
          <ZipDownloadPanel />

          {/* Integrazione Webdesk (Wolters Kluwer) */}
          <WebdeskPanel />
        </div>

        {/* COLONNA DX: logo + preview */}
        <div className="space-y-4">
          <section className="bg-white border border-slate-200 rounded-md p-5">
            <div className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-3 flex items-center gap-2">
              <ImageIcon className="w-4 h-4" /> Logo
            </div>
            <div className="border-2 border-dashed border-slate-300 rounded-sm p-4 flex flex-col items-center gap-3 min-h-[160px]">
              {form.logo_base64 ? (
                <img src={form.logo_base64} alt="Logo" className="max-h-24 max-w-full" />
              ) : (
                <div className="text-slate-400 text-sm italic">Nessun logo caricato</div>
              )}
              <label className="mt-auto inline-flex items-center gap-2 px-3 py-2 bg-slate-800 hover:bg-slate-900 text-white text-xs font-semibold rounded-sm cursor-pointer">
                <Upload className="w-4 h-4" />
                {uploading ? "Caricamento…" : "Carica logo"}
                <input
                  type="file"
                  accept="image/png,image/jpeg,image/svg+xml,image/webp"
                  onChange={(e) => handleLogo(e.target.files?.[0])}
                  data-testid="imp-logo-input"
                  className="hidden"
                />
              </label>
              {form.logo_base64 && (
                <button
                  onClick={() => set("logo_base64", "")}
                  className="text-[11px] text-rose-600 hover:underline"
                >
                  Rimuovi logo
                </button>
              )}
              <p className="text-[10px] text-slate-500 text-center">PNG, JPG, SVG, WebP · max 2MB</p>
            </div>
          </section>

          <section className="bg-white border border-slate-200 rounded-md p-4" data-testid="imp-preview">
            <div className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-2">Anteprima header</div>
            <div className="rounded-sm overflow-hidden border border-slate-200" style={{ background: form.colore_primario || "#0f172a" }}>
              <div className="p-4 flex items-center gap-3">
                {form.logo_base64 ? (
                  <img src={form.logo_base64} alt="" className="h-8 w-8 bg-white p-0.5 rounded-sm" />
                ) : (
                  <div className="w-8 h-8 flex items-center justify-center bg-white text-slate-900 font-black rounded-sm">
                    {(form.nome_breve || "E")[0].toUpperCase()}
                  </div>
                )}
                <div className="min-w-0">
                  <div className="text-white text-sm font-bold tracking-wide truncate">
                    {form.nome_breve || form.ragione_sociale || "ETHOS"}
                  </div>
                  <div className="text-white/60 text-[10px] uppercase tracking-widest truncate">
                    {form.ragione_sociale || ""}
                  </div>
                </div>
              </div>
              <div className="px-4 py-2 flex items-center gap-2" style={{ background: form.colore_accento || "#f97316" }}>
                <div className="text-white text-[10px] font-bold uppercase tracking-widest">Colore accento</div>
              </div>
            </div>
          </section>
        </div>
      </div>

      {/* Save bar */}
      <div className="sticky bottom-0 bg-white/95 backdrop-blur border-t border-slate-200 -mx-4 -mb-4 lg:-mx-8 lg:-mb-8 px-4 py-3 lg:px-8 flex justify-end gap-2">
        <button
          onClick={() => setForm(company)}
          className="px-4 py-2 text-sm font-semibold border border-slate-300 rounded-sm hover:bg-slate-50"
        >
          Ripristina
        </button>
        <button
          onClick={save}
          disabled={saving}
          data-testid="imp-save"
          className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold bg-orange-600 hover:bg-orange-700 disabled:opacity-60 text-white rounded-sm"
        >
          <Save className="w-4 h-4" />
          {saving ? "Salvataggio…" : "Salva impostazioni"}
        </button>
      </div>
    </div>
  );
}
